import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PlayerSlot — per-file-type viewer router.
 *
 * Maps a GetFileResponse to the correct React viewer component using
 * resolveViewerType(). For video/audio, declaratively renders the
 * <video>/<audio> element (with <source> children) in JSX and hands a
 * stable useId()-generated id to HekatePlayer via mediaElementId.
 *
 * Handles per-type routing for the multi-column player world.
 *
 * aria-controls approach: id-only.
 *   - We generate a stable id via React.useId() and pass it to both the
 *     <video>/<audio> element and to HekatePlayer as mediaElementId.
 *   - We do NOT pass mediaElementRef to HekatePlayer so we stay on the
 *     "exactly one of id/ref" happy path (no warning).
 *   - A local mediaRef is still kept for useAutoResume, which needs to
 *     snapshot the paused state synchronously during render.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import HekatePlayer from '../player/HekatePlayer';
import HekateImageViewer from '../image-viewer/HekateImageViewer';
import HekateDocumentViewer from '../document-viewer/HekateDocumentViewer';
import HekateTextViewer from '../text-viewer/HekateTextViewer';
import HekateCommentStream from '../comment-stream/HekateCommentStream';
import HekateDownloadOverlay from '../download-overlay/HekateDownloadOverlay';
import SyncDebugOverlay from './SyncDebugOverlay';
import { resolveViewerType } from './resolveViewerType';
import { useAutoResume } from './useAutoResume';
// ── Helpers ──────────────────────────────────────────────────────────────
function getLinkId() {
    const w = window;
    if (w.jsconnect?.link?.id)
        return String(w.jsconnect.link.id);
    return null;
}
function getProjectPerms() {
    const w = window;
    const project = w.jsconnect?.project ?? {};
    return {
        canDownload: !!project.can_download,
        canShare: !!project.can_share,
        isAdmin: !!project.is_admin,
    };
}
function buildDownloadUrl(fileId) {
    const linkId = getLinkId();
    return `download?id=${fileId}` + (linkId ? `&link=${encodeURIComponent(linkId)}` : '');
}
function widthOrDefault(data) {
    return data.metadata.width > 0 ? data.metadata.width : 960;
}
function heightOrDefault(data) {
    return data.metadata.height > 0 ? data.metadata.height : 540;
}
function fileTypeName(data) {
    switch (data.file_type_id) {
        case 1: return 'Video';
        case 2: return 'Audio';
        case 3: return 'Image';
        case 5: return 'Comment Stream';
        case 6: return 'Document';
        default: {
            const ext = data.file_extension || '';
            if (ext.toLowerCase() === 'croom')
                return 'CROOM Document';
            return ext ? `${ext.toUpperCase()} File` : 'File';
        }
    }
}
function fileIconUrl(data) {
    return data.thumbnails.thumbnail_url || '/img/Unknown_Doc.svg';
}
function mimeWithCodec(mimeType, mimeCodec) {
    return mimeCodec ? `${mimeType}; codecs="${mimeCodec}"` : mimeType;
}
// ── Global callbacks wired into HekatePlayer ─────────────────────────────
//
// These bridge the React player into page-level global helpers
// (keyboard shortcuts, comments, annotation editor). They are defined at
// module level so their references stay stable across re-renders, and each
// call is guarded with a typeof check so jest/SSR/link pages don't blow up
// when the globals aren't present.
/**
 * Pre-seek guard: if an annotation editor is currently mounted and has
 * unsaved changes, prompt the user to discard them. Returning false
 * cancels the seek (HekatePlayer honors the boolean).
 */
function legacyOnBeforeSeek() {
    const w = window;
    const h = w.annotationEditorHandle;
    if (h && h.api && h.api.isDirty && h.api.isDirty()) {
        if (window.confirm('You have unsaved annotation changes. Discard them?')) {
            try {
                h.unmount();
            }
            catch (_) { }
            w.annotationEditorHandle = null;
            return true;
        }
        return false;
    }
    return true;
}
/**
 * onMounted: initialise the page-level keyboard-shortcut handler once the
 * player is mounted. Invokes the globals `setupKeyboardShortcuts()` and
 * `setupHoldLabel()` when they are present on the page.
 */
function legacyOnPlayerMounted() {
    const w = window;
    if (typeof w.setupKeyboardShortcuts === 'function') {
        try {
            w.setupKeyboardShortcuts();
        }
        catch (_) { }
    }
    if (typeof w.setupHoldLabel === 'function') {
        try {
            w.setupHoldLabel();
        }
        catch (_) { }
    }
}
/**
 * onPlay: when the user starts playback, release any "held" state on the
 * comment field (the global `unhold()` helper in comments.js). Guarded to
 * avoid firing when the comment field has pending content.
 */
function legacyOnPlay() {
    const w = window;
    const commentBox = document.getElementById('comment-field');
    if (commentBox && typeof commentBox.value === 'string' && commentBox.value.trim().length === 0) {
        if (typeof w.unhold === 'function') {
            try {
                w.unhold();
            }
            catch (_) { }
        }
    }
}
// ── Component ────────────────────────────────────────────────────────────
// Stage style for the image viewer. Uses an explicit height rather than
// aspectRatio so images aren't forced into 16:9. The image viewer
// handles fit-to-container sizing internally via its zoom system.
const IMAGE_STAGE_STYLE = {
    width: '100%',
    height: '80vh',
    maxHeight: '80vh',
    minHeight: 240,
    display: 'flex',
    overflow: 'hidden',
};
// Stage style for document, text, comment-stream, and download viewers.
// No aspect-ratio constraint — these viewers benefit from more vertical
// space, especially when comparing side-by-side. Uses a fixed 80vh height
// so they fill most of the viewport.
const DOC_TEXT_STAGE_STYLE = {
    width: '100%',
    height: '80vh',
    maxHeight: '80vh',
    minHeight: 240,
    display: 'flex',
    overflow: 'hidden',
};
// Download-only files are a compact call-to-action, not a document canvas.
const DOWNLOAD_STAGE_STYLE = {
    width: '100%',
    minHeight: 220,
    display: 'flex',
    overflow: 'hidden',
};
export function PlayerSlot({ fileData, colId, isActive = true, onActivate, onMediaEnded, autoplay = true, onViewerReady, syncedSession, onMediaReady }) {
    const syncedSessionManager = typeof window !== 'undefined' ? window.hekateSyncedSession?.manager : undefined;
    const mediaRef = useRef(null);
    const stageRef = useRef(null);
    const hekatePlayerApiRef = useRef(null);
    const imageViewerRef = useRef(null);
    const documentViewerRef = useRef(null);
    const textViewerRef = useRef(null);
    const mediaElementId = useId();
    const imageElementId = useId();
    const stageId = useId();
    const viewerType = resolveViewerType(fileData);
    // Debug stats overlay — enabled by setting localStorage key
    // "debug.player.playbackStats" to "true". Hidden developer option,
    // not exposed in the UI.
    const [showDebugStats, setShowDebugStats] = useState(() => {
        try {
            return localStorage.getItem('debug.player.playbackStats') === 'true';
        }
        catch {
            return false;
        }
    });
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'debug.player.playbackStats') {
                setShowDebugStats(e.newValue === 'true');
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    // Snapshot playback state across file changes for the video/audio branch.
    // Harmless no-op for other viewer types since the ref stays null.
    useAutoResume(mediaRef, fileData.id);
    // Fire onViewerReady after mount / after the ref has been populated.
    // Keyed on viewerType + fileData.id so it re-fires when the column
    // swaps between viewer types (e.g. a version switch that changes the
    // file type) or between files of the same type.
    useEffect(() => {
        if (!onViewerReady)
            return;
        // download_only overrides the normal viewer type — always report 'download'.
        if (fileData.download_only) {
            onViewerReady({ type: 'download', api: null });
            return;
        }
        switch (viewerType) {
            case 'video':
            case 'audio':
                onViewerReady({ type: viewerType, api: hekatePlayerApiRef.current });
                break;
            case 'image':
                onViewerReady({ type: 'image', api: imageViewerRef.current });
                break;
            case 'document':
                onViewerReady({ type: 'document', api: documentViewerRef.current });
                break;
            case 'text':
                onViewerReady({ type: 'text', api: textViewerRef.current });
                break;
            case 'commentstream':
                onViewerReady({ type: 'commentstream', api: null });
                break;
            case 'download':
            default:
                onViewerReady({ type: 'download', api: null });
                break;
        }
        // `onViewerReady` is intentionally excluded from the dep array — the
        // manager wraps it once per mount in `createDefaultMounter`, so the
        // reference is stable for the life of the column, and adding it would
        // force the effect to re-run on every parent rerender and potentially
        // re-fire with a now-stale ref during an unrelated prop change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerType, fileData.id, fileData.download_only]);
    // Download-only override: when the server flags a file as download-only,
    // short-circuit the normal viewer routing and show a download overlay
    // regardless of the file's actual type.
    if (fileData.download_only) {
        const perms = getProjectPerms();
        const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
        const linkId = getLinkId();
        return (_jsx("div", { className: "player-stage player-stage--download", style: DOWNLOAD_STAGE_STYLE, children: _jsx(HekateDownloadOverlay, { fileTitle: fileData.title, fileSize: fileData.size, fileTypeName: fileTypeName(fileData), ...(linkId ? { linkId } : {}), iconUrl: fileIconUrl(fileData), downloadUrl: downloadUrl, preventDownload: !perms.canDownload, preventDownloadMessage: "This file has been set to download only.", width: widthOrDefault(fileData), height: heightOrDefault(fileData) }) }));
    }
    switch (viewerType) {
        case 'video':
        case 'audio': {
            const linkId = getLinkId();
            const perms = getProjectPerms();
            const downloadUrl = buildDownloadUrl(fileData.id);
            const isVideo = viewerType === 'video';
            const poster = isVideo ? fileData.thumbnails.poster_url ?? undefined : undefined;
            // Build <source> children (proxies first, then original).
            const sources = [];
            for (const proxy of fileData.proxies) {
                sources.push(_jsx("source", { src: proxy.url, type: mimeWithCodec(proxy.mime_type, proxy.mime_codec) }, `p-${proxy.url}`));
            }
            if (fileData.original_file_url) {
                // mime_codec is only populated for video files; audio uses plain mime_type.
                const type = isVideo
                    ? mimeWithCodec(fileData.mime_type, fileData.mime_codec)
                    : fileData.mime_type;
                sources.push(_jsx("source", { src: fileData.original_file_url, type: type }, `o-${fileData.original_file_url}`));
            }
            // Set width/height attributes and aspect-ratio so the browser
            // reserves the correct space before metadata loads. Without this,
            // the video starts at 0 height and jumps when loadedmetadata fires.
            const videoWidth = widthOrDefault(fileData);
            const videoHeight = heightOrDefault(fileData);
            const mediaElement = isVideo ? (_jsx("video", { id: mediaElementId, ref: mediaRef, referrerPolicy: "no-referrer-when-downgrade", crossOrigin: "use-credentials", tabIndex: 2, ...{ 'x-webkit-airplay': 'allow' }, preload: "auto", poster: poster, controlsList: "nodownload", controls: true, playsInline: true, width: videoWidth, height: videoHeight, style: { aspectRatio: `${videoWidth} / ${videoHeight}`, width: '100%', height: 'auto' }, children: sources })) : (_jsx("audio", { id: mediaElementId, ref: mediaRef, crossOrigin: "use-credentials", tabIndex: 2, preload: "auto", controlsList: "nodownload", controls: true, children: sources }));
            return (_jsxs("div", { id: stageId, tabIndex: 1, ref: stageRef, className: "player-stage", style: isVideo ? { '--video-ar-num': String(videoWidth / videoHeight) } : undefined, onClick: onActivate, children: [mediaElement, _jsx(HekatePlayer, { ref: hekatePlayerApiRef, mediaElementId: mediaElementId, fullscreenTargetRef: stageRef, mediaType: isVideo ? 'video' : 'audio', fileId: fileData.id, fps: fileData.timing.fps, startFrame: fileData.timing.start_frame, dropFrame: fileData.timing.drop_frame, canDownload: perms.canDownload, fileTitle: fileData.title, fileTypeName: isVideo ? 'Video' : 'Audio', fileSize: fileData.size, waveformColor: "var(--qc-paper)", isActive: isActive, ...(isActive ? { sidePanelElementId: 'comment-tabs' } : {}), sidePanelWidth: 400, sidePanelTooltip: "Show / hide comments", autoplay: autoplay, ...(perms.canDownload && downloadUrl ? { downloadUrl } : {}), ...(linkId ? { linkId } : {}), ...(!isVideo && fileData.thumbnails.waveform_url
                            ? { waveformUrl: fileData.thumbnails.waveform_url }
                            : {}), ...(fileData.thumbnails.thumbnail_hoverscrub_url
                            ? { thumbnailSpriteUrl: fileData.thumbnails.thumbnail_hoverscrub_url }
                            : {}), onBeforeSeek: legacyOnBeforeSeek, onMounted: legacyOnPlayerMounted, onPlay: legacyOnPlay, onEnded: onMediaEnded, ...(onMediaReady ? { onMediaReady } : {}), ...(syncedSession ? { syncedSession } : {}), ...(syncedSessionManager ? { syncedSessionManager } : {}) }), _jsx(SyncDebugOverlay, { visible: showDebugStats, mediaRef: mediaRef, role: isActive ? 'source' : 'follower', colId: colId })] }));
        }
        case 'image': {
            const linkId = getLinkId();
            const perms = getProjectPerms();
            const downloadUrl = buildDownloadUrl(fileData.id);
            const url = fileData.original_file_url ?? '';
            return (_jsxs("div", { id: stageId, ref: stageRef, className: "player-stage", style: IMAGE_STAGE_STYLE, onClick: onActivate, children: [_jsx("img", { id: imageElementId, draggable: false, crossOrigin: "use-credentials", style: { display: 'none' }, src: url, alt: fileData.title }), _jsx(HekateImageViewer, { ref: imageViewerRef, imageElementId: imageElementId, isActive: isActive, ...(isActive ? { sidePanelElementId: 'comment-tabs' } : {}), sidePanelWidth: 400, sidePanelTooltip: "Show / hide comments", ...(linkId ? { linkId } : {}), canDownload: perms.canDownload, fileTitle: fileData.title, fileTypeName: "Image", fileSize: fileData.size, ...(perms.canDownload && downloadUrl ? { downloadUrl } : {}), onMounted: legacyOnPlayerMounted })] }));
        }
        case 'document': {
            const perms = getProjectPerms();
            const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
            const linkId = getLinkId();
            return (_jsx("div", { className: "player-stage", style: DOC_TEXT_STAGE_STYLE, onClick: onActivate, children: _jsx(HekateDocumentViewer, { ref: documentViewerRef, fileUrl: fileData.original_file_url ?? '', fileId: fileData.id, fileTitle: fileData.title, width: widthOrDefault(fileData), height: heightOrDefault(fileData), ...(linkId ? { linkId } : {}), canDownload: perms.canDownload, downloadUrl: downloadUrl, fileTypeName: "PDF Document", fileSize: fileData.size, ...(isActive ? { sidePanelElementId: 'comment-tabs' } : {}), sidePanelWidth: 400, sidePanelTooltip: "Show / hide comments" }) }));
        }
        case 'text': {
            const perms = getProjectPerms();
            const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
            const linkId = getLinkId();
            return (_jsx("div", { className: "player-stage", style: DOC_TEXT_STAGE_STYLE, onClick: onActivate, children: _jsx(HekateTextViewer, { ref: textViewerRef, fileUrl: fileData.original_file_url ?? '', fileTitle: fileData.title, fileSize: fileData.size, width: widthOrDefault(fileData), height: heightOrDefault(fileData), ...(linkId ? { linkId } : {}), downloadUrl: downloadUrl, fileTypeName: "Text File", ...(isActive ? { sidePanelElementId: 'comment-tabs' } : {}), sidePanelWidth: 400, sidePanelTooltip: "Show / hide comments" }) }));
        }
        case 'commentstream': {
            return (_jsx("div", { className: "player-stage", style: DOC_TEXT_STAGE_STYLE, onClick: onActivate, children: _jsx(HekateCommentStream, { fileTitle: fileData.title }) }));
        }
        case 'download':
        default: {
            // The download viewer fills the whole column's stage. PlayerColumn
            // binds the activation click on the column wrapper itself for this
            // viewer type (the user explicitly requested this exception, since
            // there's no other selectable element inside).
            const perms = getProjectPerms();
            const downloadUrl = perms.canDownload ? buildDownloadUrl(fileData.id) : null;
            const linkId = getLinkId();
            return (_jsx("div", { className: "player-stage player-stage--download", style: DOWNLOAD_STAGE_STYLE, children: _jsx(HekateDownloadOverlay, { fileTitle: fileData.title, fileSize: fileData.size, fileTypeName: fileTypeName(fileData), ...(linkId ? { linkId } : {}), iconUrl: fileIconUrl(fileData), downloadUrl: downloadUrl, preventDownload: !perms.canDownload, width: widthOrDefault(fileData), height: heightOrDefault(fileData) }) }));
        }
    }
}
export default PlayerSlot;
